<script>
/* eslint no-console: 0 */
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';

import InboxMembersAPI from '../../../../api/inboxMembers';
import TeamsAPI from '../../../../api/teams';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import router from '../../../index';
import PageHeader from '../SettingsSubPageHeader.vue';
import { useVuelidate } from '@vuelidate/core';
import { vOnClickOutside } from '@vueuse/components';

export default {
  components: {
    PageHeader,
    NextButton,
    TagInput,
    DropdownMenu,
  },
  directives: { onClickOutside: vOnClickOutside },
  validations: {
    selectedAgentIds: {
      isEmpty() {
        return !!this.selectedAgentIds.length;
      },
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      selectedAgentIds: [],
      isCreating: false,
      isAddingTeam: false,
      showTeamDropdown: false,
    };
  },
  computed: {
    ...mapGetters({
      agentList: 'agents/getAgents',
      teamList: 'teams/getTeams',
    }),
    selectedAgentNames() {
      return this.selectedAgentIds.map(
        id => this.agentList.find(a => a.id === id)?.name ?? ''
      );
    },
    agentMenuItems() {
      return this.agentList
        .filter(({ id }) => !this.selectedAgentIds.includes(id))
        .map(({ id, name, thumbnail, avatar_url }) => ({
          label: name,
          value: id,
          action: 'select',
          thumbnail: { name, src: thumbnail || avatar_url || '' },
        }));
    },
    teamMenuItems() {
      return this.teamList.map(({ id, name }) => ({
        action: 'add_team',
        value: id,
        label: name,
        icon: 'i-lucide-users',
      }));
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('teams/get');
  },
  methods: {
    handleAgentAdd({ value }) {
      if (!this.selectedAgentIds.includes(value)) {
        this.selectedAgentIds.push(value);
      }
    },
    handleAgentRemove(index) {
      this.selectedAgentIds.splice(index, 1);
    },
    async addTeamMembers({ value }) {
      if (this.isAddingTeam) return;

      this.isAddingTeam = true;
      try {
        const { data: members } = await TeamsAPI.getAgents({ teamId: value });
        this.selectedAgentIds = [
          ...new Set([
            ...this.selectedAgentIds,
            ...members.map(member => member.id),
          ]),
        ];
        this.showTeamDropdown = false;
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.ADD.AGENTS.ADD_TEAM_ERROR'));
      } finally {
        this.isAddingTeam = false;
      }
    },
    async addAgents() {
      this.isCreating = true;
      const inboxId = this.$route.params.inbox_id;

      try {
        await InboxMembersAPI.update({
          inboxId,
          agentList: this.selectedAgentIds,
        });
        router.replace({
          name: 'settings_inbox_finish',
          params: {
            page: 'new',
            inbox_id: this.$route.params.inbox_id,
          },
        });
      } catch (error) {
        useAlert(error.message);
      }
      this.isCreating = false;
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <form class="flex flex-wrap flex-col mx-0" @submit.prevent="addAgents()">
      <div class="w-full">
        <PageHeader
          :header-title="$t('INBOX_MGMT.ADD.AGENTS.TITLE')"
          :header-content="$t('INBOX_MGMT.ADD.AGENTS.DESC')"
        />
      </div>
      <div>
        <div class="w-full mb-4">
          <label :class="{ error: v$.selectedAgentIds.$error }">
            {{ $t('INBOX_MGMT.ADD.AGENTS.TITLE') }}
            <div
              class="rounded-xl outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-strong px-2 py-2"
            >
              <TagInput
                :model-value="selectedAgentNames"
                :placeholder="$t('INBOX_MGMT.ADD.AGENTS.PICK_AGENTS')"
                :menu-items="agentMenuItems"
                show-dropdown
                skip-label-dedup
                @add="handleAgentAdd"
                @remove="handleAgentRemove"
              />
            </div>
            <div
              v-on-click-outside="() => (showTeamDropdown = false)"
              class="relative mt-3"
            >
              <NextButton
                type="button"
                :label="$t('INBOX_MGMT.ADD.AGENTS.ADD_TEAM')"
                icon="i-lucide-users"
                slate
                faded
                sm
                :is-loading="isAddingTeam"
                @click="showTeamDropdown = !showTeamDropdown"
              />
              <DropdownMenu
                v-if="showTeamDropdown"
                :menu-items="teamMenuItems"
                class="top-full ltr:left-0 rtl:right-0 z-50 mt-2 max-h-64 min-w-64"
                @action="addTeamMembers"
              />
              <p class="mt-2 mb-0 text-xs text-n-slate-10">
                {{ $t('INBOX_MGMT.ADD.AGENTS.ADD_TEAM_HELP') }}
              </p>
            </div>
            <span v-if="v$.selectedAgentIds.$error" class="message">
              {{ $t('INBOX_MGMT.ADD.AGENTS.VALIDATION_ERROR') }}
            </span>
          </label>
        </div>
        <div class="w-full">
          <NextButton
            type="submit"
            :is-loading="isCreating"
            solid
            blue
            :label="$t('INBOX_MGMT.AGENTS.BUTTON_TEXT')"
          />
        </div>
      </div>
    </form>
  </div>
</template>
